import "./conversations.css";
import useAppContext from "../../hooks/useAppContext";
import Page from "../../components/page/Page";
import React, {useEffect, useState} from "react";
import useUserContext from "../../hooks/useUserContext";
import useToast from "../../hooks/useToast";
import {ToastSkin} from "../../context/ToastContext";
import {getSubscribedClients} from "../../api/getSubscribedClients";
import {getShopSegments} from "../../api/getShopSegments";
import {ConversationRow, Segment} from "../../types/ConversationProps";
import ConversationRoomList from "./ConversationRoomList";
import ConversationRoom from "./ConversationRoom";
import SegmentList from "./SegmentList";
import ClientPanel from "../../components/clientPanel/ClientPanel";
import {Switch, Tooltip} from "antd";
import {updateWhatsappConversationAiFirst} from "../../api/updateWhatsappConversationAiFirst";

export enum CONVERSATION_VIEWS {
    CUSTOMER = 'customer',
    SEGMENT = 'segment',
}

const Conversations = () => {
    const { data: { conversations, conversationMessages, shop }, actions: { setCurrentConversationId, loadConversationMessages, addMessageToConversation, setConversationAsSeen } } = useAppContext();
    const { selectedDomain } = useUserContext();
    const [roomSearchKey, setRoomSearchKey] = useState('');
    const [roomFilter, setRoomFilter] = useState('all');
    const [currentConversation, setCurrentConversation] = useState<ConversationRow | null>(null);
    const [currentSegment, setCurrentSegment] = useState<Segment | null>(null);
    const { showToast } = useToast();
    const [allCustomers, setAllCustomers] = useState<any[] | null>(null);
    const [segments, setSegments] = useState<Segment[] | null>(null);
    const [isLoadingSegments, setIsLoadingSegments] = useState(false);
    const [clientPanelId, setClientPanelId] = useState<number | undefined>(undefined);
    const [isUpdatingConversationMode, setIsUpdatingConversationMode] = useState(false);
    const isAiEligible = !!shop.data?.pricingPlan?.withAI;
    const isWhatsappAiUiTestShop = shop.data?.id === 24;
    const [isAiFirst, setIsAiFirst] = useState(false);

    const getClients = async () => {
        if(!selectedDomain) return;
        const subscribedClients = await getSubscribedClients(selectedDomain);
    }

    const unSelectConversation = () => {
        setCurrentConversation(null);
        setCurrentConversationId(null);
    }

    const getSegments = async () => {
        if(!selectedDomain) return;
        setIsLoadingSegments(true);
        const segmentsResponse = await getShopSegments(selectedDomain);
        if(!segmentsResponse.data && segmentsResponse.data.errorMessage) {
            showToast({ skin: ToastSkin.error, text: 'Failed to load segments' });
        } else {
            setSegments(segmentsResponse.data);
        }
        setIsLoadingSegments(false);
    }

    const onSegmentSelection = (segment: Segment) => {
        setCurrentSegment(segment);
    }

    const openClientPanel = (clientId: number) => {
        setClientPanelId(clientId);
    }

    const closeClientPanel = () => {
        setClientPanelId(undefined);
    }

    useEffect(() => {
        if(!currentConversation?.id) return;
        const current = conversations.data.find((conversation: any) => conversation.id === currentConversation?.id)
        setCurrentConversation(current);
        setCurrentConversationId(current.id);
    }, [conversations]);

    useEffect(() => {
        return () => {
            setCurrentConversation(null);
            setCurrentConversationId(null);
        }
    }, []);

    useEffect(() => {
        // getClients();
        if(roomFilter === 'segments' && segments === null) {
            getSegments();
        }
    }, [selectedDomain, roomFilter]);

    useEffect(() => {
        window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "CHAT_OPEN", open: !!currentConversation }));
    }, [currentConversation]);

    useEffect(() => {
        setIsAiFirst(!!shop.data?.whatsappConversationAiFirst);
    }, [shop.data?.whatsappConversationAiFirst]);

    return (
        <Page
            headerClassName="conversation-header-container"
            className="conversations-page-container"
            title="Whatsapp Conversations"
            subtitle="Connect directly with your clients on WhatsApp. If a client responds to your marketing message, you have 24 hours to chat freely. Use this time to share special deals, coupons, or anything else needed to close the sale. No templates or approvals required."
            titleRight={shop.isLoaded && isWhatsappAiUiTestShop ? (
                <div className="flex activation-toggle widget-page-header-toggle">
                    <Tooltip title={isAiEligible ? "" : "Upgrade to an AI-supported plan to enable this."}>
                        <div>
                            <Switch
                                value={isAiFirst}
                                disabled={isUpdatingConversationMode || !isAiEligible}
                                loading={isUpdatingConversationMode}
                                onChange={async (checked) => {
                                    if (!selectedDomain || !isAiEligible) return;
                                    setIsUpdatingConversationMode(true);
                                    const response = await updateWhatsappConversationAiFirst(selectedDomain, checked);
                                    if (response.status && response.status >= 400) {
                                        showToast({ skin: ToastSkin.error, text: "Failed to update the WhatsApp AI mode." });
                                    } else {
                                        setIsAiFirst(checked);
                                    }
                                    setIsUpdatingConversationMode(false);
                                }}
                            />
                        </div>
                    </Tooltip>
                    <div className="column">
                        <span>{isAiFirst ? 'AI assistant' : 'Human team'}</span>
                        <small>{isAiFirst ? 'New WhatsApp chats start with AI.' : 'New WhatsApp chats go straight to your team.'}</small>
                    </div>
                </div>
            ) : null}
        >
            <div id="conversations-container" className="conversations-container">
                <div className="chat-rooms-container">
                    <div className="chat-rooms-filters-container">
                        <div className="search-container">
                            <input placeholder="Search" type="text" value={roomSearchKey} onChange={(event) => setRoomSearchKey(event.target.value)} />
                        </div>
                        <div className="filters-container">
                            <div className={`filter-button ${roomFilter === 'all' ? 'selected' : ''}`} onClick={() => setRoomFilter('all')}>All</div>
                            <div className={`filter-button ${roomFilter === 'active' ? 'selected' : ''}`} onClick={() => setRoomFilter('active')}>Active</div>
                            {/*<div className={`filter-button ${roomFilter === 'segments' ? 'selected' : ''}`} onClick={() => setRoomFilter('segments')}>Segments</div>*/}
                        </div>
                    </div>
                    <div className="chat-rooms-list">
                        {roomFilter === 'segments' ?
                            <SegmentList currentSegment={currentSegment} isLoadingSegments={isLoadingSegments} segments={segments} roomSearchKey={roomSearchKey} selectSegment={onSegmentSelection} /> :
                            <ConversationRoomList
                                roomFilter={roomFilter}
                                roomSearchKey={roomSearchKey}
                                setCurrentConversation={setCurrentConversation}
                                currentConversation={currentConversation}
                            />
                        }
                    </div>
                </div>
                <ConversationRoom openClientPanel={openClientPanel} currentConversation={currentConversation} currentSegment={currentSegment} view={roomFilter === 'segments' ? CONVERSATION_VIEWS.SEGMENT : CONVERSATION_VIEWS.CUSTOMER} unSelectConversation={unSelectConversation} />
            </div>
            <ClientPanel clientId={clientPanelId} onClose={closeClientPanel} onConversationClick={(conv) => {
                setCurrentConversation(conv);
                closeClientPanel();
            }} />
        </Page>
    );
}

export default Conversations;
